import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,

  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Group as TeamIcon,

  Close as CloseIcon,
  Star as StarIcon
} from '@mui/icons-material';

import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { surgicalTeamsService } from '../../services/surgicalTeamsService';
import { useUserData } from '../../hooks/useUserData';

const SurgicalTeamManagement = () => {
  const { user } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  // Get default facility ID - prioritize user's defaultFacility, then first userFacility
  const getDefaultFacilityId = useCallback(() => {
    if (user?.defaultFacility?.id && user.defaultFacility.id !== '') {
      return user.defaultFacility.id;
    }
    if (userFacilities && userFacilities.length > 0) {
      return userFacilities[0].id;
    }
    return '';
  }, [user, userFacilities]);
  
  const defaultFacilityId = getDefaultFacilityId();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', 'view'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [teams, setTeams] = useState([]);

  // Memoize loadTeams to avoid ESLint dependency warning
  const loadTeams = useCallback(async () => {
    if (!defaultFacilityId) {
      setTeams([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const teamsData = await surgicalTeamsService.getAllTeams(defaultFacilityId);
      setTeams(teamsData);
    } catch (err) {
      setError('Failed to load teams: ' + err.message);
      console.error('Error loading teams:', err);
    } finally {
      setLoading(false);
    }
  }, [defaultFacilityId]);

  // Load teams when facility changes
  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    leadSurgeon: '',
    members: [],
    availability: ''
  });

  const [availableMembers] = useState([
    { id: 1, name: 'Dr. Sarah Wilson', role: 'Surgeon', specialty: 'Cardiac' },
    { id: 2, name: 'Dr. Michael Chen', role: 'Surgeon', specialty: 'Cardiac' },
    { id: 3, name: 'Nurse Jennifer Smith', role: 'Nurse', specialty: 'General' },
    { id: 4, name: 'Nurse Robert Johnson', role: 'Nurse', specialty: 'General' },
    { id: 5, name: 'Dr. Emily Davis', role: 'Anesthesiologist', specialty: 'Anesthesia' },
    { id: 6, name: 'Dr. James Brown', role: 'Surgeon', specialty: 'Orthopedic' },
    { id: 7, name: 'Dr. Lisa Garcia', role: 'Surgeon', specialty: 'Orthopedic' },
    { id: 8, name: 'Nurse David Wilson', role: 'Nurse', specialty: 'General' },
    { id: 9, name: 'Nurse Maria Rodriguez', role: 'Nurse', specialty: 'General' },
    { id: 10, name: 'Dr. Thomas Lee', role: 'Anesthesiologist', specialty: 'Anesthesia' }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'inactive': return '#757575';
      case 'on-call': return '#2196f3';
      case 'off-duty': return '#ff9800';
      default: return '#757575';
    }
  };



  const handleOpenDialog = (type, team = null) => {
    setDialogType(type);
    setSelectedTeam(team);
    if (type === 'add') {
      setFormData({
        name: '',
        specialty: '',
        leadSurgeon: '',
        members: [],
        availability: ''
      });
    } else if (team) {
      setFormData({
        name: team.name,
        specialty: team.specialty,
        leadSurgeon: team.leadSurgeon,
        members: team.members,
        availability: team.availability
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTeam(null);
  };

  const handleSubmit = async () => {
    if (!defaultFacilityId) {
      setError('No default facility available');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const teamData = {
        ...formData,
        facilityId: defaultFacilityId
      };

      if (dialogType === 'add') {
        await surgicalTeamsService.createTeam(teamData);
        setSuccess('Team added successfully');
        await loadTeams(); // Reload teams list
      } else if (dialogType === 'edit' && selectedTeam) {
        await surgicalTeamsService.updateTeam(selectedTeam.id, teamData);
        setSuccess('Team updated successfully');
        await loadTeams(); // Reload teams list
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save team: ' + err.message);
      console.error('Error saving team:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await surgicalTeamsService.deleteTeam(teamId);
      setSuccess('Team deleted successfully');
      await loadTeams(); // Reload teams list
    } catch (err) {
      setError('Failed to delete team: ' + err.message);
      console.error('Error deleting team:', err);
    } finally {
      setLoading(false);
    }
  };

  const TeamCard = ({ team }) => (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: getStatusColor(team.status) }}>
              <TeamIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {team.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {team.specialty}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={team.status}
            size="small"
            sx={{
              bgcolor: getStatusColor(team.status),
              color: 'white',
              fontWeight: 500,
              textTransform: 'capitalize'
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Lead Surgeon: {team.leadSurgeon}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Members: {team.members.length} • Availability: {team.availability}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          {team.currentCase !== 'No current case' ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Current: {team.currentCase}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Available
              </Typography>
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary">
            Next: {team.nextCase}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Performance
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption">
              {team.totalSurgeries} surgeries
            </Typography>
            <Chip
              label={`${team.successRate}% success`}
              size="small"
              variant="outlined"
              color="success"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenDialog('view', team)}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Team">
              <IconButton size="small" onClick={() => handleOpenDialog('edit', team)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Team">
              <IconButton size="small" onClick={() => handleDeleteTeam(team.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StarIcon sx={{ fontSize: '1rem', color: '#ffc107' }} />
            <Typography variant="caption" color="text.secondary">
              {team.successRate}%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Surgical Team Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage surgical teams, assignments, and scheduling
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
            sx={{ borderRadius: 2 }}
            disabled={!defaultFacilityId}
          >
            Add Team
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50', mb: 1 }}>
              {teams.filter(t => t.status === 'active').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Teams
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196f3', mb: 1 }}>
              {teams.reduce((acc, team) => acc + team.members.length, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Members
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
              {teams.reduce((acc, team) => acc + team.totalSurgeries, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Surgeries
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0', mb: 1 }}>
              {(teams.reduce((acc, team) => acc + team.successRate, 0) / teams.length).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg Success Rate
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Team Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : teams.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TeamIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Teams Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {defaultFacilityId ? 'No surgical teams have been created for this facility yet.' : 'No default facility available.'}
          </Typography>
          {defaultFacilityId && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
            >
              Add First Team
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {teams.map((team) => (
            <Grid item xs={12} sm={6} md={4} key={team.id}>
              <TeamCard team={team} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TeamIcon />
            {dialogType === 'add' ? 'Add Surgical Team' : 
             dialogType === 'edit' ? 'Edit Surgical Team' : 'Team Details'}
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogType === 'view' && selectedTeam ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Team Information</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Team Name" secondary={selectedTeam.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Specialty" secondary={selectedTeam.specialty} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Lead Surgeon" secondary={selectedTeam.leadSurgeon} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Status" secondary={selectedTeam.status} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Availability" secondary={selectedTeam.availability} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Performance</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Total Surgeries" secondary={selectedTeam.totalSurgeries} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Success Rate" secondary={`${selectedTeam.successRate}%`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Current Case" secondary={selectedTeam.currentCase} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Next Case" secondary={selectedTeam.nextCase} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Team Members</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Experience</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTeam.members.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell>{member.name}</TableCell>
                            <TableCell>{member.role}</TableCell>
                            <TableCell>
                              <Chip
                                label={member.status}
                                size="small"
                                sx={{
                                  bgcolor: getStatusColor(member.status),
                                  color: 'white',
                                  textTransform: 'capitalize'
                                }}
                              />
                            </TableCell>
                            <TableCell>{member.experience}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Team Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Specialty</InputLabel>
                  <Select
                    value={formData.specialty}
                    label="Specialty"
                    onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  >
                    <MenuItem value="Cardiac Surgery">Cardiac Surgery</MenuItem>
                    <MenuItem value="Orthopedic Surgery">Orthopedic Surgery</MenuItem>
                    <MenuItem value="General Surgery">General Surgery</MenuItem>
                    <MenuItem value="Neurosurgery">Neurosurgery</MenuItem>
                    <MenuItem value="Ophthalmology">Ophthalmology</MenuItem>
                    <MenuItem value="Pediatric Surgery">Pediatric Surgery</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lead Surgeon"
                  value={formData.leadSurgeon}
                  onChange={(e) => setFormData({ ...formData, leadSurgeon: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Availability"
                  value={formData.availability}
                  onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                  placeholder="e.g., Mon-Fri, 8AM-6PM"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={availableMembers}
                  getOptionLabel={(option) => `${option.name} (${option.role})`}
                  value={formData.members}
                  onChange={(event, newValue) => setFormData({ ...formData, members: newValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team Members"
                      placeholder="Select team members"
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {dialogType !== 'view' && (
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'add' ? 'Add Team' : 'Save Changes'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default SurgicalTeamManagement; 